import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  PieChart, BarChart3, TrendingUp, TrendingDown, Calculator, Building2,
  Users, MapPin, DollarSign, Target, Plus, RefreshCw, Download,
  AlertCircle, CheckCircle, XCircle, Settings, Database, Link, Upload, FileText, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart as RechartsPie, Pie, Legend
} from 'recharts';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const AccountingAdvancedMIS = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('cost-allocation');
  
  // Date filters
  const [dateFrom, setDateFrom] = useState('2024-04-01');
  const [dateTo, setDateTo] = useState('2025-03-31');
  
  // Cost Allocation State
  const [allocationRules, setAllocationRules] = useState([]);
  const [allocationResults, setAllocationResults] = useState(null);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    expense_ledger_id: '',
    allocation_method: 'fixed_percentage',
    allocation_percentages: []
  });
  
  // Profitability State
  const [projectProfitability, setProjectProfitability] = useState(null);
  const [clientProfitability, setClientProfitability] = useState(null);
  const [profitabilityMatrix, setProfitabilityMatrix] = useState(null);
  
  // Tally Integration State
  const [tallyConnections, setTallyConnections] = useState([]);
  const [showTallyDialog, setShowTallyDialog] = useState(false);
  const [newTallyConnection, setNewTallyConnection] = useState({
    tally_company_name: '',
    tally_server_url: 'http://localhost',
    tally_port: 9000,
    sync_ledgers: true,
    sync_vouchers: true
  });
  
  // File Import State
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importConnection, setImportConnection] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importDataType, setImportDataType] = useState('auto');
  const [importReplaceExisting, setImportReplaceExisting] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Master data
  const [ledgers, setLedgers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchMasterData();
      fetchAllocationRules();
      fetchTallyConnections();
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/accounting/companies');
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [ledgersRes, projectsRes, locationsRes] = await Promise.all([
        api.get(`/accounting/ledgers?company_id=${selectedCompany}`),
        api.get(`/accounting/projects?company_id=${selectedCompany}`),
        api.get(`/accounting/locations?company_id=${selectedCompany}`)
      ]);
      setLedgers(ledgersRes.data);
      setProjects(projectsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAllocationRules = async () => {
    try {
      const response = await api.get(`/accounting/cost-allocation/rules?company_id=${selectedCompany}`);
      setAllocationRules(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTallyConnections = async () => {
    try {
      const response = await api.get(`/accounting/tally/connections?company_id=${selectedCompany}`);
      setTallyConnections(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const executeAllocation = async () => {
    try {
      setLoading(true);
      const response = await api.post(
        `/accounting/cost-allocation/execute/${selectedCompany}?date_from=${dateFrom}&date_to=${dateTo}`
      );
      setAllocationResults(response.data);
      toast.success('Cost allocation executed successfully');
    } catch (error) {
      toast.error('Failed to execute allocation');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectProfitability = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/accounting/reports/profitability/project/${selectedCompany}?date_from=${dateFrom}&date_to=${dateTo}`
      );
      setProjectProfitability(response.data);
    } catch (error) {
      toast.error('Failed to fetch project profitability');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientProfitability = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/accounting/reports/profitability/client/${selectedCompany}?date_from=${dateFrom}&date_to=${dateTo}`
      );
      setClientProfitability(response.data);
    } catch (error) {
      toast.error('Failed to fetch client profitability');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitabilityMatrix = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/accounting/reports/profitability/matrix/${selectedCompany}?date_from=${dateFrom}&date_to=${dateTo}`
      );
      setProfitabilityMatrix(response.data);
    } catch (error) {
      toast.error('Failed to fetch profitability matrix');
    } finally {
      setLoading(false);
    }
  };

  const createAllocationRule = async () => {
    try {
      await api.post('/accounting/cost-allocation/rules', {
        company_id: selectedCompany,
        ...newRule,
        is_active: true
      });
      toast.success('Allocation rule created');
      setShowRuleDialog(false);
      fetchAllocationRules();
      setNewRule({ name: '', expense_ledger_id: '', allocation_method: 'fixed_percentage', allocation_percentages: [] });
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const deleteAllocationRule = async (ruleId) => {
    try {
      await api.delete(`/accounting/cost-allocation/rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchAllocationRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const createTallyConnection = async () => {
    try {
      await api.post('/accounting/tally/connections', {
        company_id: selectedCompany,
        ...newTallyConnection
      });
      toast.success('Tally connection created');
      setShowTallyDialog(false);
      fetchTallyConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create connection');
    }
  };

  const testTallyConnection = async (connectionId) => {
    try {
      const response = await api.post(`/accounting/tally/test-connection/${connectionId}`);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      fetchTallyConnections();
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  const openImportDialog = (conn) => {
    setImportConnection(conn);
    setImportFile(null);
    setImportDataType('auto');
    setImportReplaceExisting(false);
    setShowImportDialog(true);
  };

  const handleFileImport = async () => {
    if (!importConnection || !importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('data_type', importDataType);
      formData.append('replace_existing', importReplaceExisting);

      const response = await api.post(
        `/accounting/tally-connections/${importConnection.id}/import-file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status === 'success') {
        const imported = response.data.imported;
        const total = Object.values(imported).reduce((a, b) => a + b, 0);
        toast.success(`Successfully imported ${total} items: ${JSON.stringify(imported)}`);
        setShowImportDialog(false);
        fetchTallyConnections();
      } else {
        toast.error(response.data.errors?.join(', ') || 'Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = async (connectionId, dataType) => {
    try {
      const response = await api.get(
        `/accounting/tally-connections/${connectionId}/download-template/${dataType}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tally_${dataType}_template.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const expenseLedgers = ledgers.filter(l => l.account_type === 'expense');

  if (loading && companies.length === 0) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Advanced MIS</h1>
            <p className="text-gray-500">Cost allocation, profitability analysis, and Tally integration</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm font-medium">Company</Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">From Date</Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">To Date</Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-1" />
              </div>
              <div className="flex items-end">
                <Badge variant="outline" className="h-10 px-4 flex items-center gap-2">
                  <Calculator className="h-4 w-4" />
                  FY 2024-25
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="cost-allocation" className="gap-2">
              <Calculator className="h-4 w-4" />
              Cost Allocation
            </TabsTrigger>
            <TabsTrigger value="project-profitability" className="gap-2">
              <Target className="h-4 w-4" />
              Project P&L
            </TabsTrigger>
            <TabsTrigger value="client-profitability" className="gap-2">
              <Users className="h-4 w-4" />
              Client P&L
            </TabsTrigger>
            <TabsTrigger value="tally-integration" className="gap-2">
              <Database className="h-4 w-4" />
              Tally Integration
            </TabsTrigger>
          </TabsList>

          {/* Cost Allocation Tab */}
          <TabsContent value="cost-allocation" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Cost Allocation Rules</h2>
              <div className="flex gap-2">
                <Button onClick={executeAllocation} disabled={allocationRules.length === 0}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Execute Allocation
                </Button>
                <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Rule
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Allocation Rule</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Rule Name</Label>
                        <Input value={newRule.name} onChange={e => setNewRule({...newRule, name: e.target.value})} />
                      </div>
                      <div>
                        <Label>Expense Ledger</Label>
                        <Select value={newRule.expense_ledger_id} onValueChange={v => setNewRule({...newRule, expense_ledger_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Select Ledger" /></SelectTrigger>
                          <SelectContent>
                            {expenseLedgers.map(l => (
                              <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Allocation Method</Label>
                        <Select value={newRule.allocation_method} onValueChange={v => setNewRule({...newRule, allocation_method: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="headcount">By Headcount</SelectItem>
                            <SelectItem value="area_based">By Area</SelectItem>
                            <SelectItem value="revenue_ratio">By Revenue Ratio</SelectItem>
                            <SelectItem value="fixed_percentage">Fixed Percentage</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={createAllocationRule}>Create Rule</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Rules List */}
            <Card>
              <CardContent className="pt-6">
                {allocationRules.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No allocation rules defined. Create a rule to allocate costs.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allocationRules.map(rule => (
                      <div key={rule.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{rule.name}</p>
                          <p className="text-sm text-gray-500">Method: {rule.allocation_method.replace('_', ' ')}</p>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant={rule.is_active ? "default" : "secondary"}>
                            {rule.is_active ? "Active" : "Inactive"}
                          </Badge>
                          <Button variant="ghost" size="sm" onClick={() => deleteAllocationRule(rule.id)}>
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Allocation Results */}
            {allocationResults && (
              <Card>
                <CardHeader>
                  <CardTitle>Allocation Results ({allocationResults.period.from} to {allocationResults.period.to})</CardTitle>
                </CardHeader>
                <CardContent>
                  {allocationResults.allocations.length === 0 ? (
                    <p className="text-gray-500">No expenses to allocate in this period.</p>
                  ) : (
                    <div className="space-y-6">
                      {allocationResults.allocations.map((alloc, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex justify-between items-center mb-4">
                            <div>
                              <p className="font-semibold">{alloc.rule_name}</p>
                              <p className="text-sm text-gray-500">{alloc.ledger_name} - {alloc.method.replace('_', ' ')}</p>
                            </div>
                            <p className="text-xl font-bold text-indigo-600">₹{alloc.total_amount.toLocaleString()}</p>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {alloc.breakdown.map((item, i) => (
                              <div key={i} className="bg-gray-50 p-3 rounded">
                                <p className="text-sm text-gray-600">{item.project || item.location}</p>
                                <p className="font-semibold">₹{item.amount.toLocaleString()}</p>
                                <p className="text-xs text-gray-500">{item.percentage}%</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Project Profitability Tab */}
          <TabsContent value="project-profitability" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Project Profitability Analysis</h2>
              <div className="flex gap-2">
                <Button onClick={fetchProjectProfitability}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Analyze
                </Button>
                <Button variant="outline" onClick={fetchProfitabilityMatrix}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Matrix
                </Button>
              </div>
            </div>

            {projectProfitability && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <DollarSign className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Revenue</p>
                          <p className="text-xl font-bold">₹{projectProfitability.summary.total_revenue.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <TrendingUp className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Profit</p>
                          <p className="text-xl font-bold">₹{projectProfitability.summary.total_profit.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Target className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Profit Margin</p>
                          <p className="text-xl font-bold">{projectProfitability.summary.overall_margin}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Building2 className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Profitable Projects</p>
                          <p className="text-xl font-bold">{projectProfitability.summary.profitable_projects}/{projectProfitability.summary.projects_count}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Project-wise Profitability</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={projectProfitability.projects.filter(p => p.id !== 'unallocated').slice(0, 10)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                          <Legend />
                          <Bar dataKey="revenue" name="Revenue" fill="#4F46E5" />
                          <Bar dataKey="direct_costs" name="Direct Costs" fill="#EF4444" />
                          <Bar dataKey="net_profit" name="Net Profit" fill="#10B981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Projects Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Project Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4">Project</th>
                            <th className="text-left py-3 px-4">Client</th>
                            <th className="text-right py-3 px-4">Revenue</th>
                            <th className="text-right py-3 px-4">Direct Costs</th>
                            <th className="text-right py-3 px-4">Indirect Costs</th>
                            <th className="text-right py-3 px-4">Net Profit</th>
                            <th className="text-right py-3 px-4">Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectProfitability.projects.map(proj => (
                            <tr key={proj.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{proj.name}</td>
                              <td className="py-3 px-4 text-gray-500">{proj.client}</td>
                              <td className="py-3 px-4 text-right">₹{proj.revenue.toLocaleString()}</td>
                              <td className="py-3 px-4 text-right text-red-600">₹{proj.direct_costs.toLocaleString()}</td>
                              <td className="py-3 px-4 text-right text-orange-600">₹{proj.indirect_costs.toLocaleString()}</td>
                              <td className={`py-3 px-4 text-right font-semibold ${proj.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ₹{proj.net_profit.toLocaleString()}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Badge variant={proj.profit_margin >= 20 ? "default" : proj.profit_margin >= 0 ? "secondary" : "destructive"}>
                                  {proj.profit_margin}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Profitability Matrix */}
            {profitabilityMatrix && (
              <Card>
                <CardHeader>
                  <CardTitle>Project vs Location Profitability Matrix</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-4">Project</th>
                          {profitabilityMatrix.locations.map(loc => (
                            <th key={loc} className="text-right py-3 px-4">{loc}</th>
                          ))}
                          <th className="text-right py-3 px-4 font-bold">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profitabilityMatrix.matrix.map((row, idx) => (
                          <tr key={idx} className={`border-b ${row.project === 'Total' ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}`}>
                            <td className="py-3 px-4 font-medium">{row.project}</td>
                            {profitabilityMatrix.locations.map(loc => (
                              <td key={loc} className={`py-3 px-4 text-right ${(row[loc] || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ₹{(row[loc] || 0).toLocaleString()}
                              </td>
                            ))}
                            <td className={`py-3 px-4 text-right font-semibold ${(row.Total || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ₹{(row.Total || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Client Profitability Tab */}
          <TabsContent value="client-profitability" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Client Profitability Analysis</h2>
              <Button onClick={fetchClientProfitability}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Analyze
              </Button>
            </div>

            {clientProfitability && (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Users className="h-8 w-8 mx-auto mb-2 text-indigo-600" />
                      <p className="text-2xl font-bold">{clientProfitability.summary.total_clients}</p>
                      <p className="text-sm text-gray-500">Total Clients</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-600" />
                      <p className="text-2xl font-bold">{clientProfitability.summary.profitable_clients}</p>
                      <p className="text-sm text-gray-500">Profitable</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <DollarSign className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                      <p className="text-2xl font-bold">₹{(clientProfitability.summary.total_revenue / 100000).toFixed(1)}L</p>
                      <p className="text-sm text-gray-500">Total Revenue</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <TrendingUp className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
                      <p className="text-2xl font-bold">₹{(clientProfitability.summary.total_profit / 100000).toFixed(1)}L</p>
                      <p className="text-sm text-gray-500">Total Profit</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Revenue Distribution by Client</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={clientProfitability.clients.filter(c => c.revenue > 0)}
                            dataKey="revenue"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          >
                            {clientProfitability.clients.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Clients Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Client Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4">Client</th>
                            <th className="text-center py-3 px-4">Projects</th>
                            <th className="text-right py-3 px-4">Revenue</th>
                            <th className="text-right py-3 px-4">Costs</th>
                            <th className="text-right py-3 px-4">Profit</th>
                            <th className="text-right py-3 px-4">Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clientProfitability.clients.map(client => (
                            <tr key={client.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{client.name}</td>
                              <td className="py-3 px-4 text-center">{client.projects_count}</td>
                              <td className="py-3 px-4 text-right">₹{client.revenue.toLocaleString()}</td>
                              <td className="py-3 px-4 text-right text-red-600">₹{client.costs.toLocaleString()}</td>
                              <td className={`py-3 px-4 text-right font-semibold ${client.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ₹{client.profit.toLocaleString()}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Badge variant={client.margin >= 20 ? "default" : client.margin >= 0 ? "secondary" : "destructive"}>
                                  {client.margin}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Tally Integration Tab */}
          <TabsContent value="tally-integration" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Tally Prime Integration</h2>
              <Dialog open={showTallyDialog} onOpenChange={setShowTallyDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Connection
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Configure Tally Connection</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Tally Company Name</Label>
                      <Input 
                        value={newTallyConnection.tally_company_name} 
                        onChange={e => setNewTallyConnection({...newTallyConnection, tally_company_name: e.target.value})}
                        placeholder="e.g., T&N Business Services"
                      />
                    </div>
                    <div>
                      <Label>Tally Server URL</Label>
                      <Input 
                        value={newTallyConnection.tally_server_url} 
                        onChange={e => setNewTallyConnection({...newTallyConnection, tally_server_url: e.target.value})}
                        placeholder="http://localhost"
                      />
                    </div>
                    <div>
                      <Label>Port</Label>
                      <Input 
                        type="number"
                        value={newTallyConnection.tally_port} 
                        onChange={e => setNewTallyConnection({...newTallyConnection, tally_port: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createTallyConnection}>Save Connection</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Instructions Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900">Tally Prime Setup Instructions</p>
                    <ul className="mt-2 space-y-1 text-sm text-blue-800">
                      <li>1. Open Tally Prime on your server</li>
                      <li>2. Go to F12 Configuration &gt; Data Configuration &gt; Enable ODBC Server</li>
                      <li>3. Set port to 9000 (or your preferred port)</li>
                      <li>4. Configure firewall to allow connections on the port</li>
                      <li>5. Add the connection details below and test</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Connections List */}
            <Card>
              <CardHeader>
                <CardTitle>Configured Connections</CardTitle>
              </CardHeader>
              <CardContent>
                {tallyConnections.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No Tally connections configured yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tallyConnections.map(conn => (
                      <div key={conn.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold">{conn.tally_company_name}</p>
                            <p className="text-sm text-gray-500">{conn.tally_server_url}:{conn.tally_port}</p>
                          </div>
                          <Badge variant={
                            conn.sync_status === 'success' ? 'default' : 
                            conn.sync_status === 'failed' ? 'destructive' : 'secondary'
                          }>
                            {conn.sync_status === 'success' ? <CheckCircle className="h-3 w-3 mr-1" /> :
                             conn.sync_status === 'failed' ? <XCircle className="h-3 w-3 mr-1" /> :
                             <AlertCircle className="h-3 w-3 mr-1" />}
                            {conn.sync_status}
                          </Badge>
                        </div>
                        {conn.last_sync_error && (
                          <p className="text-sm text-red-600 mt-2">{conn.last_sync_error}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-4">
                          <Button size="sm" variant="outline" onClick={() => testTallyConnection(conn.id)}>
                            <Link className="h-4 w-4 mr-2" />
                            Test
                          </Button>
                          <Button size="sm" variant="outline" onClick={async () => {
                            try {
                              toast.info('Syncing ledgers from Tally...');
                              const response = await api.post(`/accounting/tally/sync-ledgers/${conn.id}`);
                              if (response.data.status === 'success') {
                                toast.success(response.data.message);
                              } else {
                                toast.error(response.data.message);
                              }
                              fetchTallyConnections();
                            } catch (error) {
                              toast.error('Sync failed');
                            }
                          }}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Sync Ledgers
                          </Button>
                          <Button size="sm" variant="outline" onClick={async () => {
                            try {
                              toast.info('Syncing vouchers from Tally...');
                              const response = await api.post(`/accounting/tally/sync-vouchers/${conn.id}?date_from=${dateFrom}&date_to=${dateTo}`);
                              if (response.data.status === 'success') {
                                toast.success(response.data.message);
                              } else {
                                toast.error(response.data.message);
                              }
                              fetchTallyConnections();
                            } catch (error) {
                              toast.error('Sync failed');
                            }
                          }}>
                            <Download className="h-4 w-4 mr-2" />
                            Sync Vouchers
                          </Button>
                          <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => openImportDialog(conn)}>
                            <Upload className="h-4 w-4 mr-2" />
                            Import from File
                          </Button>
                          <Button size="sm" onClick={async () => {
                            try {
                              toast.info('Pushing vouchers to Tally...');
                              const response = await api.post(`/accounting/tally/push-vouchers/${conn.id}?date_from=${dateFrom}&date_to=${dateTo}`);
                              if (response.data.status === 'success') {
                                toast.success(response.data.message);
                              } else {
                                toast.error(response.data.message);
                              }
                              fetchTallyConnections();
                            } catch (error) {
                              toast.error('Push failed');
                            }
                          }}>
                            <TrendingUp className="h-4 w-4 mr-2" />
                            Push to Tally
                          </Button>
                        </div>
                        
                        {/* Show last import info */}
                        {conn.last_sync_results?.source === 'file_import' && (
                          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm">
                            <p className="font-medium text-green-800">
                              <FileSpreadsheet className="inline h-4 w-4 mr-1" />
                              Last imported from: {conn.last_sync_results.file}
                            </p>
                            <p className="text-green-600 text-xs">
                              {Object.entries(conn.last_sync_results.imported || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* File Import Dialog */}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-green-600" />
                    Import Tally Data from File
                  </DialogTitle>
                  <DialogDescription>
                    Upload an exported XML or Excel file from Tally to sync data without direct network access.
                  </DialogDescription>
                </DialogHeader>
                
                {importConnection && (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="font-medium text-blue-800">{importConnection.tally_company_name}</p>
                      <p className="text-sm text-blue-600">{importConnection.tally_server_url}:{importConnection.tally_port}</p>
                    </div>
                    
                    {/* File Upload */}
                    <div className="space-y-2">
                      <Label>Select File</Label>
                      <div 
                        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept=".xml,.xlsx,.xls"
                          onChange={(e) => setImportFile(e.target.files[0])}
                        />
                        {importFile ? (
                          <div className="flex items-center justify-center gap-2">
                            {importFile.name.endsWith('.xml') ? (
                              <FileText className="h-8 w-8 text-orange-500" />
                            ) : (
                              <FileSpreadsheet className="h-8 w-8 text-green-500" />
                            )}
                            <div className="text-left">
                              <p className="font-medium">{importFile.name}</p>
                              <p className="text-sm text-gray-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                            <p className="text-gray-600">Click to select file</p>
                            <p className="text-sm text-gray-400">XML or Excel (.xlsx)</p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Data Type Selection */}
                    <div className="space-y-2">
                      <Label>Data Type</Label>
                      <Select value={importDataType} onValueChange={setImportDataType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto-detect</SelectItem>
                          <SelectItem value="ledgers">Ledgers (Chart of Accounts)</SelectItem>
                          <SelectItem value="vouchers">Vouchers (Transactions)</SelectItem>
                          <SelectItem value="stock_items">Stock Items (Inventory)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Replace Existing Option */}
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="replace-existing" 
                        checked={importReplaceExisting}
                        onCheckedChange={setImportReplaceExisting}
                      />
                      <Label htmlFor="replace-existing" className="text-sm cursor-pointer">
                        Replace existing data (clears previous imports)
                      </Label>
                    </div>
                    
                    {/* Download Templates */}
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium mb-2">Download Templates:</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => downloadTemplate(importConnection.id, 'ledgers')}>
                          <Download className="h-3 w-3 mr-1" />
                          Ledgers
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => downloadTemplate(importConnection.id, 'vouchers')}>
                          <Download className="h-3 w-3 mr-1" />
                          Vouchers
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => downloadTemplate(importConnection.id, 'stock_items')}>
                          <Download className="h-3 w-3 mr-1" />
                          Stock Items
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleFileImport} 
                    disabled={!importFile || importLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {importLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Import Data
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Export for Tally */}
            <Card>
              <CardHeader>
                <CardTitle>Export Transactions for Tally</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 mb-4">Export transactions in Tally-compatible XML format for manual import.</p>
                <Button variant="outline" onClick={async () => {
                  try {
                    const response = await api.get(`/accounting/tally/export-for-tally/${selectedCompany}?date_from=${dateFrom}&date_to=${dateTo}`);
                    const blob = new Blob([response.data.xml_content], { type: 'application/xml' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `tally_import_${dateFrom}_to_${dateTo}.xml`;
                    a.click();
                    toast.success(`Exported ${response.data.transactions_count} transactions`);
                  } catch (error) {
                    toast.error('Export failed');
                  }
                }}>
                  <Download className="h-4 w-4 mr-2" />
                  Export XML for Tally Import
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AccountingAdvancedMIS;
